import { useEffect, useState } from "react";
import {
  useOrderListSections,
  useOrderListSettings,
  useSaveOrderListSettings,
  useSaveStoreBufferPreference,
  useStoreBufferPreference,
} from "../../shared/hooks/useOrderList";
import { OrderListDeliverySetting, OrderListRow } from "../../models/orderListModels";
import Loader from "rsuite/Loader";
import Message from "rsuite/Message";
import Button from "rsuite/Button";
import Modal from "rsuite/Modal";
import Header from "../common/Header";
import { getUserStoreNameFromLocalStorage, setOrderListBufferToLocalStorage } from "../../helpers/localStorageHelper";
import { FiCalendar, FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp, FiRefreshCw, FiX } from "react-icons/fi";
import "./OrderList.css";
import { formatLocalDate, getOrderListDeliveryRequests, moveToConfiguredDate, OrderListDeliveryRequest } from "../../helpers/orderListDateHelper";
import { formatOrderListQuantity } from "../../helpers/orderListQuantityHelper";
import { cloneOrderListSettings, getOrderListErrors } from "../../helpers/orderListSettingsHelper";
import { filterOrderListRows, getIngredientOptionsForCategories, pruneIngredientsForCategories, pruneUnavailableSelections } from "../../helpers/orderListFilterHelper";
import { deduplicateOrderListWarnings } from "../../helpers/orderListWarningHelper";
import OrderListSettingsPanel from "./OrderListSettingsPanel";
import OrderListMultiSelectFilter from "./OrderListMultiSelectFilter";

const isoDate = formatLocalDate;
const ALL_CATEGORIES = "__ALL_CATEGORIES__";
const normalizeBufferValue = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Number(value.toFixed(2)));
};
const toDisplayDate = (value: string): string => {
  const dateOnly = value.slice(0, 10);
  const [year, month, day] = dateOnly.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};
const toWeekday = (value: string): string => {
  const dateOnly = value.slice(0, 10);
  const [year, month, day] = dateOnly.split("-").map(Number);

  if (!year || !month || !day) {
    return "";
  }

  const parsed = new Date(year, month - 1, day);

  return parsed.toLocaleDateString("en-AU", {
    weekday: "long",
  });
};;

const OrderList = (): JSX.Element => {
  const settingsQuery = useOrderListSettings();
  const allSettings = settingsQuery.data?.settings ?? [];
  const settings = allSettings.filter(setting => setting.active);
  const [settingId, setSettingId] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(isoDate(new Date()));
  const [buffer, setBuffer] = useState(0);
  const [debouncedBuffer, setDebouncedBuffer] = useState(0);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [hasHydratedBufferPreference, setHasHydratedBufferPreference] = useState(false);
  const bufferPreferenceQuery = useStoreBufferPreference();
  const saveBufferPreference = useSaveStoreBufferPreference();
  const [editingSettings, setEditingSettings] = useState(false);
  const [draftSettings, setDraftSettings] = useState<OrderListDeliverySetting[]>([]);
  const [deletedSettingIds, setDeletedSettingIds] = useState<string[]>([]);
  const [showUnsavedSettingsPrompt, setShowUnsavedSettingsPrompt] = useState(false);
  const [expandedDeliveryIds, setExpandedDeliveryIds] = useState<string[]>([]);
  const [specificDeliveryExpanded, setSpecificDeliveryExpanded] = useState(true);
  const saveSettings = useSaveOrderListSettings();

  useEffect(() => {
    document.body.classList.add("order-list-active");
    return () => document.body.classList.remove("order-list-active");
  }, []);

  useEffect(() => {
    if (!settingId && settings.length > 0) {
      setSettingId(ALL_CATEGORIES);
    }
  }, [settingId, settings]);

  useEffect(() => {
    if (settingsQuery.data?.settings) {
      setDraftSettings(cloneOrderListSettings(settingsQuery.data.settings));
      setDeletedSettingIds([]);
    }
  }, [settingsQuery.data]);

  useEffect(() => {
    if (typeof bufferPreferenceQuery.data === "number") {
      const nextBuffer = normalizeBufferValue(bufferPreferenceQuery.data);
      setBuffer(current => (current === nextBuffer ? current : nextBuffer));
      setHasHydratedBufferPreference(true);
    }
  }, [bufferPreferenceQuery.data]);

  const configuredDays = settingId === ALL_CATEGORIES
    ? Array.from(new Set(settings.flatMap(setting => setting.deliveryDays)))
    : settings.find(setting => setting.id === settingId)?.deliveryDays ?? [];
  const deliveryRequests = getOrderListDeliveryRequests(settings, deliveryDate, settingId, ALL_CATEGORIES);
  const queryDeliveryDate = deliveryRequests[0]?.deliveryDate ?? deliveryDate;
  const deliveryRequestKey = deliveryRequests.map(request => `${request.setting.id}:${request.deliveryDate}`).join("|");
  // const firstUpcomingDeliveryId = deliveryRequests[0]?.setting.id ?? "";
  const isPast = new Date(`${queryDeliveryDate}T00:00:00`).getTime() < new Date(new Date().toDateString()).getTime();
  const dataConfigured = settingsQuery.data?.dataConfigured !== false;
  const sectionQueries = useOrderListSections(dataConfigured ? deliveryRequests : [], debouncedBuffer, forceRefresh);
  const responses = sectionQueries.map(query => query.data).filter(Boolean);
  const isLoading = sectionQueries.some(query => query.isLoading);
  const isError = sectionQueries.some(query => query.isError);
  const queryErrors = Array.from(new Set(sectionQueries
    .filter(query => query.isError)
    .flatMap(query => getOrderListErrors(query.error))));
  const missingDatasets = settingsQuery.data?.missingDatasets ?? [];
  const configurationMessage = settingsQuery.data && settingsQuery.data.dataConfigured === false
    ? `Order List data is not configured. Publish ${missingDatasets.join(", ")} in the Admin dashboard.`
    : "";

  useEffect(() => {
    // A delivery due today is already committed, so default past it to the next one.
    // Recalculates from today whenever the setting or its schedule changes, so an
    // earlier day added back to the schedule is picked up rather than kept on the old one.
    // Applies in "All Delivery Settings" mode too, since today is always historical.
    if (!settingId || configuredDays.length === 0) {
      return;
    }
    setDeliveryDate(moveToConfiguredDate(isoDate(new Date()), configuredDays, 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingId, configuredDays.join(",")]);

  useEffect(() => {
    if (settingId === ALL_CATEGORIES) {
      //setExpandedDeliveryIds(firstUpcomingDeliveryId ? [firstUpcomingDeliveryId] : []);
      // All Delivery Settings starts with every card collapsed.
      setExpandedDeliveryIds([]);
      return;
    } else if (settingId) {
      // A specifically selected delivery setting starts expanded.
      setSpecificDeliveryExpanded(true);
    }
  }, [deliveryRequestKey, /*firstUpcomingDeliveryId,*/ settingId]);

  useEffect(() => {
    if (forceRefresh) {
      setForceRefresh(false);
    }
  }, [forceRefresh]);

  useEffect(() => {
    if (!hasHydratedBufferPreference) {
      return;
    }

    setOrderListBufferToLocalStorage(buffer);
  }, [buffer, hasHydratedBufferPreference]);

  useEffect(() => {
    // Coalesce rapid buffer edits (typing, repeated clicks) into a single request instead of one per keystroke.
    const timeout = setTimeout(() => setDebouncedBuffer(buffer), 400);
    return () => clearTimeout(timeout);
  }, [buffer]);

  useEffect(() => {
    if (!hasHydratedBufferPreference || typeof bufferPreferenceQuery.data !== "number") {
      return;
    }

    const timeout = setTimeout(() => {
      if (buffer !== bufferPreferenceQuery.data) {
        saveBufferPreference.mutate(buffer);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [buffer, bufferPreferenceQuery.data, hasHydratedBufferPreference, saveBufferPreference]);
  const moveDate = (days: number): void => {
    setDeliveryDate(moveToConfiguredDate(deliveryDate, configuredDays, days > 0 ? 1 : -1));
  };
  const moveToToday = (): void => {
    setDeliveryDate(moveToConfiguredDate(isoDate(new Date()), configuredDays, 1));
  };
  const adjustBuffer = (amount: number): void => {
    setBuffer(current => normalizeBufferValue(current + amount));
  };
  const updateBufferFromInput = (raw: string): void => {
    const value = Number(raw.replace("%", ""));
    setBuffer(normalizeBufferValue(Number.isFinite(value) ? value : 0));
  };
  const saveDraftSettings = (): void => {
    saveSettings.mutate({ settings: draftSettings, deletedSettingIds }, {
      onSuccess: () => {
        setEditingSettings(false);
        setDeletedSettingIds([]);
      },
    });
  };
  // A never-saved draft entry has no server record, so it needs no delete flag.
  const deleteDraftSetting = (id: string): void => {
    setDraftSettings(current => current.filter(setting => setting.id !== id));
    if (settingsQuery.data?.settings.some(setting => setting.id === id)) {
      setDeletedSettingIds(current => current.includes(id) ? current : [...current, id]);
    }
  };
  const hasUnsavedSettingsChanges = JSON.stringify(draftSettings) !== JSON.stringify(cloneOrderListSettings(settingsQuery.data?.settings ?? []));
  const requestCloseSettings = (): void => {
    if (hasUnsavedSettingsChanges) {
      setShowUnsavedSettingsPrompt(true);
    } else {
      setEditingSettings(false);
    }
  };
  const discardSettingsChanges = (): void => {
    setDraftSettings(cloneOrderListSettings(settingsQuery.data?.settings ?? []));
    setDeletedSettingIds([]);
    setShowUnsavedSettingsPrompt(false);
    setEditingSettings(false);
  };
  const saveAndCloseSettings = (): void => {
    setShowUnsavedSettingsPrompt(false);
    saveDraftSettings();
  };
  const addSetting = (): void => {
    const id = `order-list-setting-${Date.now()}`;
    const firstCategory = settingsQuery.data?.activeCategories?.[0];
    setDraftSettings(current => [...current, {
      id,
      storeId: "",
      name: "New delivery setting",
      categories: firstCategory ? [firstCategory] : [],
      deliveryDays: [],
      active: true,
      version: 0,
      modified: new Date().toISOString(),
      modifiedBy: "",
    }]);
    setEditingSettings(true);
  };
  const responseWarnings = responses.flatMap(response => response?.warnings ?? []);
  const warnings = deduplicateOrderListWarnings(responseWarnings);
  const coverage = responses[0];

  return (
    <main className="order-list-page">
      <Header title={`SOUL ORIGIN (${getUserStoreNameFromLocalStorage()})`} subTitle="Order List" />
      <section className="order-list-settings-card" aria-label="Order List settings">
        <div className="order-list-settings-row">
          <label className="order-list-settings-label">Delivery setting
            <select value={settingId} onChange={event => setSettingId(event.target.value)}>
              <option value="">Select setting</option>
              <option value={ALL_CATEGORIES}>All Delivery Settings</option>
              {settings.map(setting => <option key={setting.id} value={setting.id}>{setting.name}</option>)}
            </select>
          </label>
          <div className="order-list-settings-buttons">
            <Button className="order-list-touch-button order-list-action-button" appearance="primary" onClick={() => editingSettings ? requestCloseSettings() : setEditingSettings(true)}>
              {editingSettings ? "Close settings" : "Manage delivery settings"}
            </Button>
          </div>
        </div>
        <OrderListSettingsPanel
          draftSettings={draftSettings}
          activeCategories={settingsQuery.data?.activeCategories ?? []}
          open={editingSettings}
          saving={saveSettings.isLoading}
          error={saveSettings.error}
          onClose={requestCloseSettings}
          onSave={saveDraftSettings}
          onChange={setDraftSettings}
          onDelete={deleteDraftSetting}
          onAddSetting={addSetting}
        />
      </section>

      <div className="order-list-controls-shell">
      <div className="order-list-controls-row">
        <section className="order-list-control-panel order-list-date-panel" aria-label="Delivery date controls">
          <div className="order-list-panel-title">Delivery Date</div>
          <div className="order-list-panel-content">
            <div className="order-list-date-navigation">
              <Button className="order-list-touch-button order-list-icon-button" appearance="primary" aria-label="Previous delivery date" onClick={() => moveDate(-1)}><FiChevronLeft /></Button>
              <div className="order-list-date-display">{toDisplayDate(deliveryDate)} ({toWeekday(deliveryDate)})</div>
              <Button className="order-list-touch-button order-list-icon-button" appearance="primary" aria-label="Next delivery date" onClick={() => moveDate(1)}><FiChevronRight /></Button>
            </div>
            <div className="order-list-date-actions">
              <Button className="order-list-touch-button order-list-today-button" appearance="primary" onClick={moveToToday}>Back to Next Delivery</Button>
              <Button className="order-list-touch-button order-list-refresh-button" appearance="primary" disabled={isPast || isLoading} onClick={() => setForceRefresh(true)}>Refresh Order List</Button>
            </div>
            <div className="order-list-date-coverage" aria-label="Coverage summary">
              <FiCalendar aria-hidden="true" />
              <div>
                <span>Coverage</span>
                <strong>{coverage
                  ? `${toDisplayDate(coverage.coverFromDate)} (${toWeekday(coverage.coverFromDate)} ${coverage.coverFromPrepTime}) → ${toDisplayDate(coverage.coverToDate)} (${toWeekday(coverage.coverToDate)} ${coverage.coverToPrepTime})`
                  : "No coverage available"}</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="order-list-control-panel order-list-buffer-panel" aria-label="Buffer controls">
          <div className="order-list-panel-title">Buffer</div>
          <div className="order-list-panel-content">
            <div className="order-list-buffer-values">
              <Button className="order-list-buffer-button" appearance="subtle" disabled={isPast || buffer === 0} onClick={() => adjustBuffer(-5)}>-5%</Button>
              <input className="order-list-buffer-input" aria-label="Buffer percentage" type="text" inputMode="decimal" value={`${buffer}%`} disabled={isPast} onChange={event => updateBufferFromInput(event.target.value)} />
              <Button className="order-list-buffer-button" appearance="primary" disabled={isPast} onClick={() => adjustBuffer(5)}>+5%</Button>
            </div>
            <Button className="order-list-buffer-reset" appearance="subtle" disabled={isPast || buffer === 0} onClick={() => setBuffer(0)}>Reset</Button>
          </div>
        </section>
      </div>
      </div>

      <Modal open={showUnsavedSettingsPrompt} onClose={() => setShowUnsavedSettingsPrompt(false)}>
        <Modal.Header><Modal.Title>Unsaved delivery setting changes</Modal.Title></Modal.Header>
        <Modal.Body>You have unsaved delivery setting changes. Save them before closing, or discard them?</Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" loading={saveSettings.isLoading} onClick={saveAndCloseSettings}>Save changes</Button>
          <Button appearance="ghost" color="red" onClick={discardSettingsChanges}>Discard changes</Button>
          <Button appearance="subtle" onClick={() => setShowUnsavedSettingsPrompt(false)}>Keep editing</Button>
        </Modal.Footer>
      </Modal>

      {settingsQuery.isLoading && <Loader center />}
      {settingsQuery.isError && <Message type="error" showIcon>Delivery settings could not be loaded.</Message>}
      {isLoading && <Loader center />}
      {configurationMessage && <Message type="info" showIcon>{configurationMessage}</Message>}
      {isError && queryErrors.map(message => <Message key={message} type="error" showIcon>{message}</Message>)}
      {!isLoading && !isError && !configurationMessage && responses.length === 0 && settingId && <Message showIcon>No order quantities for this delivery cycle.</Message>}
      
      <div className="order-list-info-row">
        {warnings.length > 0 && <details className="order-list-warnings">
          <summary><span>!</span><strong>Warnings</strong><b>{warnings.length}</b><i aria-hidden="true">&#8250;</i></summary>
          <div className="order-list-warnings__list">
            {warnings.map((warning, index) => <Message key={`${warning.code}-${warning.message}-${index}`} type="warning" showIcon>
              <div className="order-list-warning__message">{warning.message}</div>
              {warning.product && <div className="order-list-warning__context">Product: {warning.product}</div>}
              {warning.category && <div className="order-list-warning__context">Category: {warning.category}</div>}
            </Message>)}
          </div>
        </details>}
      </div>
      {dataConfigured && settingId !== ALL_CATEGORIES && deliveryRequests[0] && <section className="order-list-specific-delivery">
        <UpcomingDeliveryCard
          request={deliveryRequests[0]}
          response={sectionQueries[0]?.data}
          loading={sectionQueries[0]?.isLoading ?? false}
            error={sectionQueries[0]?.error}
          expanded={specificDeliveryExpanded}
          onToggle={() => setSpecificDeliveryExpanded(current => !current)}
        />
      </section>}
      {dataConfigured && settingId === ALL_CATEGORIES && <section className="order-list-upcoming" aria-labelledby="upcoming-deliveries-title">
        <div className="order-list-upcoming__heading">
          <div>
            <h2 id="upcoming-deliveries-title">Upcoming Deliveries</h2>
            <p>Showing next occurrence from {toDisplayDate(deliveryDate)} ({toWeekday(deliveryDate)})</p>
          </div>
        </div>
        <div className="order-list-upcoming__cards">
          {deliveryRequests.map((request, index) => <UpcomingDeliveryCard
            key={`${request.setting.id}-${request.deliveryDate}`}
            request={request}
            response={sectionQueries[index]?.data}
            loading={sectionQueries[index]?.isLoading ?? false}
            error={sectionQueries[index]?.error}
            expanded={expandedDeliveryIds.includes(request.setting.id)}
            onToggle={() => setExpandedDeliveryIds(current => current.includes(request.setting.id)
              ? current.filter(id => id !== request.setting.id)
              : [...current, request.setting.id])}
          />)}
        </div>
      </section>}
    </main>
  );
};

const OrderListTableRow = ({ row, expanded, onToggle }: { row: OrderListRow; expanded: boolean; onToggle: () => void }): JSX.Element => (
  <>
    <tr>
      <td className="order-list-table__expand"><Button appearance="subtle" aria-label={`${expanded ? "Collapse" : "Expand"} ${row.ingredient}`} onClick={onToggle}>{expanded ? <FiChevronUp /> : <FiChevronDown />}</Button></td>
      <td>{row.ingredient}</td><td>{row.category}</td>
      <td>{formatOrderListQuantity(row.baseQuantity)} {row.unit}</td><td>{formatOrderListQuantity(row.bufferQuantity)} {row.unit}</td><td><strong>{formatOrderListQuantity(row.orderQuantity)} {row.unit}</strong></td>
    </tr>
    {expanded && <tr className="order-list-breakdown"><td colSpan={6}>
      {row.directBreakdown.length > 0 && <div className="order-list-breakdown__section">
        <strong>Direct demand</strong>
        <div className="order-list-breakdown__table-wrap">
          <table className="order-list-breakdown__table">
            <thead><tr><th>Source</th><th>Product</th><th>Product qty</th><th>Ingredient qty</th></tr></thead>
            <tbody>{row.directBreakdown.map((item, index) => <tr key={`${item.product}-${item.date}-${item.prepTime}-${index}`}>
              <td>{item.source}</td>
              <td>{item.product}</td>
              <td>{formatOrderListQuantity(item.productQuantity)}</td>
              <td>{formatOrderListQuantity(item.ingredientQuantity)} {row.unit}</td>
            </tr>)}</tbody>
          </table>
        </div>
      </div>}
      {row.indirectBreakdown.length > 0 && <div className="order-list-breakdown__section">
        <strong>Made In Store demand</strong>
        <div className="order-list-breakdown__table-wrap">
          <table className="order-list-breakdown__table">
            <thead><tr><th>Source</th><th>Product</th><th>Product qty</th><th>Made-in-store item</th><th>Ingredient qty</th></tr></thead>
            <tbody>{row.indirectBreakdown.map((item, index) => <tr key={`${item.product}-${item.madeInStoreItem}-${item.date}-${item.prepTime}-${index}`}>
              <td>{item.source}</td>
              <td>{item.product}</td>
              <td>{formatOrderListQuantity(item.productQuantity)}</td>
              <td>{item.madeInStoreItem}</td>
              <td>{formatOrderListQuantity(item.childContribution)} {item.childUnit}</td>
            </tr>)}</tbody>
          </table>
        </div>
      </div>}
    </td></tr>}
  </>
);

export default OrderList;

const UpcomingDeliveryCard = ({ request, response, loading, error, expanded, onToggle }: {
  request: OrderListDeliveryRequest<OrderListDeliverySetting>;
  response?: NonNullable<ReturnType<typeof useOrderListSections>[number]["data"]>;
  loading: boolean;
  error: unknown;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element => {
  const [selectedCategories, setSelectedCategories] = useState<string[] | null>(null);
  const [selectedIngredients, setSelectedIngredients] = useState<string[] | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const originalRows = response?.rows ?? [];
  const categories = Array.from(new Set(originalRows.map(row => row.category)))
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
  const ingredientOptions = getIngredientOptionsForCategories(originalRows, selectedCategories);
  const weekday = toWeekday(request.deliveryDate);
  const filteredRows = filterOrderListRows(originalRows, selectedCategories, selectedIngredients);
  const hasActiveFilters = selectedCategories !== null || selectedIngredients !== null;

  // Restoring/validating applied selections whenever the underlying data changes: drop anything
  // no longer available (e.g. a renamed category) instead of leaving a stale filter applied.
  useEffect(() => {
    setSelectedCategories(current => pruneUnavailableSelections(current, categories));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);
  // Runs after every category Apply (or chip removal) as well as data changes, so ingredient
  // selections that no longer belong to the applied categories are dropped, valid ones kept.
  useEffect(() => {
    setSelectedIngredients(current => pruneIngredientsForCategories(originalRows, selectedCategories, current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, selectedCategories]);

  const clearAllFilters = (): void => {
    setSelectedCategories(null);
    setSelectedIngredients(null);
  };
  const removeCategoryFilter = (value: string): void => {
    setSelectedCategories(current => {
      const next = (current ?? []).filter(item => item !== value);
      return next.length === 0 ? null : next;
    });
  };
  const removeIngredientFilter = (value: string): void => {
    setSelectedIngredients(current => {
      const next = (current ?? []).filter(item => item !== value);
      return next.length === 0 ? null : next;
    });
  };

  return <article className={`order-list-delivery-card${expanded ? " order-list-delivery-card--expanded" : ""}`}>
    <button className="order-list-delivery-card__header" type="button" aria-expanded={expanded} onClick={onToggle}>
      <strong>{request.setting.name}</strong>
      <span>Delivery Date: <b>{toDisplayDate(request.deliveryDate)} ({weekday})</b></span>
      <span>Coverage: <b>{response
        ? `${toDisplayDate(response.coverFromDate)} (${toWeekday(response.coverFromDate)} ${response.coverFromPrepTime}) → ${toDisplayDate(response.coverToDate)} (${toWeekday(response.coverToDate)} ${response.coverToPrepTime})`
        : "Loading..."}</b></span>
      <span className="order-list-delivery-card__badge">{weekday} Delivery</span>
      {expanded ? <FiChevronUp aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}
    </button>
    {expanded && <div className="order-list-delivery-card__body">
      {loading && <Loader center />}
      {error && getOrderListErrors(error).map(message => <Message key={message} type="error" showIcon>{message}</Message>)}
      {response && <>
        <div className="order-list-table-controls order-list-delivery-card__filters">
          <span className="order-list-filter-label">Filters</span>
          <OrderListMultiSelectFilter
            label="Categories"
            options={categories}
            selected={selectedCategories}
            onApply={setSelectedCategories}
            ariaLabel={`${request.setting.name} category filter`}
          />
          <OrderListMultiSelectFilter
            label="Ingredients"
            options={ingredientOptions}
            selected={selectedIngredients}
            onApply={setSelectedIngredients}
            ariaLabel={`${request.setting.name} ingredient filter`}
          />
          <button type="button" className="order-list-clear-filters" onClick={clearAllFilters}>
            <FiRefreshCw aria-hidden="true" /> Clear filters
          </button>
        </div>
        {hasActiveFilters && <div className="order-list-active-filters">
          <span className="order-list-active-filters__label">Active filters:</span>
          <div className="order-list-active-filters__chips">
            {(selectedCategories ?? []).map(value => <span className="order-list-filter-chip" key={`category-${value}`}>
              {value}
              <button type="button" aria-label={`Remove ${value} category filter`} onClick={() => removeCategoryFilter(value)}><FiX aria-hidden="true" /></button>
            </span>)}
            {(selectedIngredients ?? []).map(value => <span className="order-list-filter-chip" key={`ingredient-${value}`}>
              {value}
              <button type="button" aria-label={`Remove ${value} ingredient filter`} onClick={() => removeIngredientFilter(value)}><FiX aria-hidden="true" /></button>
            </span>)}
          </div>
          <span className="order-list-active-filters__count">{filteredRows.length} ingredient{filteredRows.length === 1 ? "" : "s"} match{filteredRows.length === 1 ? "es" : ""}</span>
        </div>}
        <div className="order-list-table-wrap">
          <table className="order-list-table">
            <colgroup><col className="order-list-table__expand-column" /><col className="order-list-table__ingredient-column" /><col className="order-list-table__category-column" /><col /><col /><col /></colgroup>
            <thead><tr><th aria-label="Expand" /><th>Ingredient</th><th>Category</th><th>Base</th><th>Buffer</th><th>Order</th></tr></thead>
            <tbody>{filteredRows.map(row => {
              const rowId = `${request.setting.id}-${row.ingredient}-${row.unit}`;
              return <OrderListTableRow key={rowId} row={row} expanded={expandedRow === rowId} onToggle={() => setExpandedRow(expandedRow === rowId ? null : rowId)} />;
            })}</tbody>
          </table>
        </div>
      </>}
    </div>}
  </article>;
};